import animations from 'gsap';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MissionCard } from '../../../components/MissionCard/MissionCard';
import { ISODateToDate } from '../../../utils/formatters';
import type { MissionsDisplayProps } from './MissionsDisplay';

export function CardView(props: MissionsDisplayProps): JSX.Element {
  const [gridCss, setGridCss] = useState({
    justifyContent: '',
    gridTemplateColumns: '',
    cardsPerRow: 0,
  });
  const [containerWidth, setContainerWidth] = useState(0);

  const itemsRef = useRef<Array<HTMLDivElement | null>>([]);

  const missionCardDisplayEl = useRef<HTMLDivElement | null>(null);

  const checkContainerWidth = useCallback(() => {
    const currWidth = missionCardDisplayEl.current?.clientWidth;
    if (currWidth && currWidth !== containerWidth) {
      setContainerWidth(currWidth);
    }
  }, [containerWidth]);

  useLayoutEffect(() => {
    checkContainerWidth();
    window.addEventListener('resize', checkContainerWidth);
    return () => window.removeEventListener('resize', checkContainerWidth);
  }, [checkContainerWidth]);

  const handleResize = useCallback(() => {
    setGridCss({ justifyContent: '', gridTemplateColumns: '', cardsPerRow: 0 });
    if (missionCardDisplayEl.current) {
      if (props.missions.length === 0) {
        setGridCss({
          justifyContent: 'center',
          gridTemplateColumns: '100%',
          cardsPerRow: 0,
        });
      } else {
        const maxWidth = 412;
        const minWidth = 272;
        const halfWay = (maxWidth + minWidth) / 2;
        const cardsPerRow = Math.max(1, Math.round(containerWidth / halfWay));

        setGridCss({
          justifyContent: cardsPerRow === 1 ? 'center' : 'space-between',
          gridTemplateColumns: `repeat(${cardsPerRow}, 1fr)`,
          cardsPerRow,
        });
      }
    }
  }, [containerWidth, props.missions.length]);

  useEffect(() => {
    if (containerWidth > 0) {
      handleResize();
    }
  }, [containerWidth, handleResize]);

  useEffect(() => {
    for (let i = 0; i < itemsRef.current.length; i++) {
      animations.from(itemsRef.current[i], {
        y: '100vh',
        duration: 0.25,
        delay: (i % gridCss.cardsPerRow) * 0.12,
      });
    }
  }, [gridCss.cardsPerRow]);

  const cards = useMemo(() => {
    if (gridCss.cardsPerRow === 0) {
      return null;
    }

    return props.missions.map((mission, i) => {
      return (
        <div
          key={'mwList' + mission.id + i + props.missions.length}
          className="mission-card-wrapper"
          ref={(el) => (itemsRef.current[i] = el)}
        >
          <MissionCard
            featured={mission.featured}
            id={mission.id}
            name={mission.name}
            rewards={mission.rewards}
            inProgress={!!mission.inProgress}
            expiringDate={ISODateToDate(mission.expiringDate)}
            expiringSoon={!!mission.expiringSoon}
            repeats={!!mission.repeats}
            thumbUrl={mission.iconImageUrl}
            lockedStatus={mission.lockedStatus?.reasons}
            iLike={!!mission.iLike}
            totalLikes={mission.totalLikes ?? 0}
            setLike={props.setLike}
          />
        </div>
      );
    });
  }, [gridCss.cardsPerRow, props.missions, props.setLike]);

  return (
    <div
      ref={missionCardDisplayEl}
      style={{
        justifyContent: gridCss.justifyContent,
        gridTemplateColumns: gridCss.gridTemplateColumns,
      }}
      className="missions-card-display"
    >
      {cards}
    </div>
  );
}
